import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { BrandTheme, BrandPaletteEntry, BrandThemeHistoryEntry } from '../types/gotypes'

const BASE = `${API_BASE_URL}/brand-themes`

/** The 409 body's `conflict` block (#1265): which theme already holds the
 *  name, and how many pages / dashboards reference it. There is no overwrite
 *  path (Matt, 7 Sep): the only way past a clash is a different name. */
export interface BrandThemeConflict {
  theme_id: string
  theme_name: string
  pages_using: number
  dashboards_using: number
}

export class BrandThemeConflictError extends Error {
  readonly conflict: BrandThemeConflict
  constructor(conflict: BrandThemeConflict) {
    super(`A theme named "${conflict.theme_name}" already exists`)
    this.name = 'BrandThemeConflictError'
    this.conflict = conflict
  }
}

/** Rethrow a 409 as BrandThemeConflictError; anything else passes through. */
function rethrowConflict(err: unknown): never {
  if (axios.isAxiosError(err) && err.response?.status === 409 && err.response.data?.conflict) {
    throw new BrandThemeConflictError(err.response.data.conflict as BrandThemeConflict)
  }
  throw err
}

export const brandThemesApi = {
  async list(customer: string): Promise<BrandTheme[]> {
    const res = await axios.get<{ themes: BrandTheme[] }>(`${BASE}/${customer}`)
    return res.data.themes || []
  },

  /** 409 → BrandThemeConflictError. */
  async create(customer: string, data: { name: string; palette: Record<string, BrandPaletteEntry> }): Promise<BrandTheme> {
    try {
      const res = await axios.post<BrandTheme>(`${BASE}/${customer}`, data)
      return res.data
    } catch (err) {
      rethrowConflict(err)
    }
  },

  async get(customer: string, id: string): Promise<BrandTheme> {
    const res = await axios.get<BrandTheme>(`${BASE}/${customer}/${id}`)
    return res.data
  },

  /** 409 (renaming onto another theme's name) → BrandThemeConflictError. */
  async update(customer: string, id: string, data: { name: string; palette: Record<string, BrandPaletteEntry> }): Promise<BrandTheme> {
    try {
      const res = await axios.put<BrandTheme>(`${BASE}/${customer}/${id}`, data)
      return res.data
    } catch (err) {
      rethrowConflict(err)
    }
  },

  async remove(customer: string, id: string): Promise<void> {
    await axios.delete(`${BASE}/${customer}/${id}`)
  },

  /** Elevate to company-level (#1266). Manager/Realm only, as for dashboards. */
  async promote(customer: string, id: string): Promise<BrandTheme> {
    const res = await axios.post<BrandTheme>(`${BASE}/${customer}/${id}/promote`)
    return res.data
  },

  async demote(customer: string, id: string): Promise<BrandTheme> {
    const res = await axios.delete<BrandTheme>(`${BASE}/${customer}/${id}/promote`)
    return res.data
  },

  /** Who did what and when (#1279), newest first. */
  async history(customer: string, id: string): Promise<BrandThemeHistoryEntry[]> {
    const res = await axios.get<{ entries: BrandThemeHistoryEntry[] }>(`${BASE}/${customer}/${id}/history`)
    return res.data.entries || []
  },
}
